'use client';

// ОБЩИЙ РЕПОРТЕР КЛИЕНТА (владелец 25.08.2026, вместе с журналом): одна дверь наружу для
// всех, кто нашёл беду на странице, — ловец крашей (pwa-updater), сторож медленных
// переходов и зависаний (client-watch), ручная «Диагностика» в настройках.
// Раньше `report` жил приватно внутри pwa-updater.tsx — журналу и сторожу пришлось бы
// заводить второй такой же, а два репортера — это два разных капа и две разных формы
// контекста в центре ошибок.
//
// Два капа, намеренно РАЗНЫЕ:
//   • краши — 3 на загрузку (шторм повторов одной поломки не должен забивать центр);
//   • находки сторожа — по одной на ВИД раз в 5 минут (медленный переход повторяется
//     каждым тапом, а инцидент нужен один).
import { afterFirstScreen } from './first-screen';
import { journalMeta, log, snapshot } from './client-journal';
import { nativeBridge, nativeMethod } from './native-app';

const BUILD = process.env.NEXT_PUBLIC_BUILD_ID;

/**
 * РОДНОЙ ЛОГ ОБОЛОЧКИ — К КАЖДОМУ ЖУРНАЛУ (канон docs/concepts/DIAGNOSTICS-CANON.md,
 * владелец 06.09.2026). До этого дня родной лог прикладывала только кнопка «Диагностика»,
 * а автожурналы (перезапуск, сторож) уезжали без него — и разбор претензии «шапка под
 * часами» (сборки 138–146) вёлся вслепую: строки с геометрией экрана лежали в телефоне и
 * на сервер не попадали. Метод НОВЫЙ — только через nativeMethod (капкан старых сборок).
 */
export async function collectNativeLines(): Promise<string[] | undefined> {
  if (typeof window === 'undefined' || !nativeMethod('diagLog')) return undefined;
  try {
    const r = await nativeBridge()?.diagLog?.();
    return Array.isArray(r?.lines) && r.lines.length ? r.lines : undefined;
  } catch {
    return undefined; // мост отбил — журнал уедет без родной части, но уедет
  }
}

/** Родной лог в журнале: последние 400 строк (кап ручки), строка не длиннее 1500 знаков. */
export const NATIVE_LINES_CAP = 400;

export function trimNativeLines(lines: readonly string[]): string[] {
  return lines.slice(-NATIVE_LINES_CAP).map((l) => (l.length > 1500 ? l.slice(0, 1500) + '…' : l));
}

/** Не чаще раза в 45 с — уход экрана не должен превращаться в ленту одинаковых журналов. */
export const AUTO_JOURNAL_GAP_MS = 45_000;
let autoAt = Number.NEGATIVE_INFINITY; // первый уход — всегда

/** true — авто-журнал можно слать прямо сейчас (окно засчитано). Чистая, под юниты. */
export function autoJournalAllowed(now = Date.now()): boolean {
  if (now - autoAt < AUTO_JOURNAL_GAP_MS) return false;
  autoAt = now;
  return true;
}

let autoBound = false;

/**
 * АВТО-ЖУРНАЛ ПО УХОДУ РОДНОГО ЭКРАНА (канон DIAGNOSTICS-CANON): оболочка шлёт событие
 * diagFlush {screen} при закрытии чата/мастера/письма/аккаунта/слоёв/окна бота — журнал страницы
 * с родным логом уезжает на сервер САМ, без кнопки. Пауза 700 мс — чтобы в лог успели
 * лечь строки самого закрытия. Идемпотентно; вне приложения и на старых сборках — пусто.
 */
export function startAutoJournal(): void {
  if (autoBound || typeof window === 'undefined') return;
  const br = nativeBridge();
  if (!br?.addListener) return;
  autoBound = true;
  void br.addListener('diagFlush', (d) => {
    const screen = String((d as { screen?: unknown } | undefined)?.screen ?? 'screen').slice(0, 24);
    if (!autoJournalAllowed()) return;
    window.setTimeout(() => void sendJournal(`screen:${screen}`), 700);
  });
}

/**
 * ЖУРНАЛ ВЕРСИЙ ПО ПОЛЬЗОВАТЕЛЮ (П139, владелец 25.09.2026: «у кого какие версии — не
 * косвенно»): один раз на старт страницы кабинета — `POST /api/cabinet/version` с веб-сборкой;
 * сборку/коммит приложения и ОС сервер читает из User-Agent. Вне кабинета (лендинг, справка)
 * сессии может не быть — не зовём. Никогда не бросает и ничего не ждёт.
 */
let versionSent = false;
export function reportVersion(): void {
  if (versionSent || typeof window === 'undefined') return;
  if (!/\/app(\/|$)/.test(location.pathname)) return;
  versionSent = true;
  // НЕ НА СТАРТЕ, А ПОСЛЕ ПЕРВОГО ЭКРАНА (П162 п.4, владелец 26.09.2026): журнал версий
  // человеку не нужен для первого экрана — запрос уходит в простой после него.
  afterFirstScreen('version', 0, sendVersion);
}

function sendVersion(): void {
  try {
    void fetch('/api/cabinet/version', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ webBuild: process.env.NEXT_PUBLIC_BUILD_ID ?? 'dev' }),
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    /* сеть/приватный режим — журнал версий не важнее страницы */
  }
}

/** Кап крашей на одну загрузку страницы. */
export const CRASH_CAP = 3;
/** Окно капа сторожа на один вид находки. */
export const WATCH_GAP_MS = 5 * 60_000;

let crashSent = 0;
const watchAt = new Map<string, number>();

/** true — этот вид находки сторожа можно репортить прямо сейчас (и окно засчитано). */
export function watchAllowed(kind: string, now = Date.now()): boolean {
  const last = watchAt.get(kind);
  if (last !== undefined && now - last < WATCH_GAP_MS) return false;
  watchAt.set(kind, now);
  return true;
}

/**
 * Репорт в центр ошибок (/api/cabinet/ui-error, под сессией). Никогда не бросает.
 * Кап 3 на загрузку — только для крашей; сторож зовёт с `capped: false` и стережёт себя
 * сам (watchAllowed), иначе шторм крашей съедал бы его находки и наоборот.
 */
export function report(
  kind: string,
  message: string,
  stack?: string,
  extra?: Record<string, unknown>,
  capped = true,
): void {
  if (typeof window === 'undefined') return;
  // В журнал находка ложится ВСЕГДА, даже когда кап съеден: журнал для того и заведён,
  // чтобы по нему было видно, что происходило на странице, а не только первые три беды.
  log('error', { k: kind, m: message.slice(0, 160) });
  if (capped) {
    if (crashSent >= CRASH_CAP) return;
    crashSent += 1;
  }
  try {
    void fetch('/api/cabinet/ui-error', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        kind,
        message: message.slice(0, 900),
        context: {
          url: window.location.pathname,
          ua: navigator.userAgent.slice(0, 200),
          stack: (stack ?? '').slice(0, 1500),
          build: BUILD ?? 'dev',
          ...(extra ?? {}),
        },
      }),
      keepalive: true,
    });
  } catch {
    /* репортер никогда не роняет страницу сам */
  }
}

/**
 * Отправить ЖУРНАЛ целиком (/api/cabinet/ui-journal) и вернуть его id — он уезжает в
 * контекст инцидента ссылкой, чтобы в админке открывалась вся лента событий, а не
 * обрезанный снимок. reason: manual (человек нажал «Отправить журнал»),
 * slow_nav | freeze | slow_boot (сторож). `lines` — родной лог приложения (diagLog).
 */
export async function sendJournal(
  reason: string,
  opts?: {
    lines?: string[];
    note?: string;
    full?: boolean;
    /** Находка сторожа: сервер запишет инцидент со ссылкой на этот журнал ОДНИМ запросом. */
    incident?: { kind: string; message: string; context?: Record<string, unknown> };
  },
): Promise<string | null> {
  if (typeof window === 'undefined') return null;
  try {
    // Родной лог — ВСЕГДА, кто бы ни слал (кнопка, сторож, перезапуск, уход экрана).
    const lines = opts?.lines ?? (await collectNativeLines());
    // КАПКАН (найден живой пробой 25.08.2026): `keepalive` у fetch ограничивает тело
    // 64 КБ на всё окно — журнал в это НЕ влезает, и запрос молча не уходит вовсе
    // (браузер отбивает его сам, сервер ничего не видит). Поэтому здесь обычный fetch:
    // журнал шлётся живой страницей (кнопка человека или сторож), а не на выгрузке.
    // Кап тела — 40 КБ: свежие события важнее, старые обрезаются (trimToBytes).
    const res = await fetch('/api/cabinet/ui-journal', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        reason,
        events: snapshot(opts?.full ? 600 : 200, 40 * 1024),
        meta: {
          ...journalMeta(),
          ...(opts?.note ? { note: opts.note.slice(0, 200) } : {}),
          ...(lines?.length ? { native: 1 } : {}),
        },
        // Родной лог — последние 400 строк (кап ручки), строка не длиннее 1500 знаков: дамп
        // стопки слоёв (dumpLayers) даёт строки по килобайту, и полное кольцо не влезало в
        // кап — «не удалось отправить» у владельца 26.08. 400, а не 250: с 06.09 в лог
        // едут хвост прошлого запуска (↩) и каждый вызов моста, 250 срезало бы перезапуск.
        ...(lines?.length ? { lines: trimNativeLines(lines) } : {}),
        ...(opts?.incident ? { incident: opts.incident } : {}),
      }),
    });
    const j = (await res.json().catch(() => null)) as { ok?: boolean; id?: string; error?: string } | null;
    if (j?.ok && j.id) {
      lastJournalError = '';
      return j.id;
    }
    // Причина отказа — человеку на экран (владелец 26.08: «не удалось отправить» без цифр
    // не разобрать, что именно отбило: размер, лимит или сеть).
    lastJournalError = `${res.status}${j?.error ? ' ' + j.error : ''}`;
    return null;
  } catch (e) {
    lastJournalError = `network ${String((e as { message?: string })?.message ?? e).slice(0, 80)}`;
    return null;
  }
}

/** Код последнего отказа ручки журнала («413 too_large», «429 too_many», «network …»); пусто — ок. */
export let lastJournalError = '';

/** Только для тестов: сбросить капы. */
export function resetReportCapsForTests(): void {
  crashSent = 0;
  watchAt.clear();
  autoAt = Number.NEGATIVE_INFINITY;
}
